import { AirParticles } from '@/components/Layout';
import { Wind, Target, Eye, Users } from 'lucide-react';

export default function About() {
  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 lg:px-12 overflow-hidden" style={{ background: 'var(--soft-white)' }} data-testid="about-hero">
        <AirParticles count={10} color="rgba(15,76,92,0.04)" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="overline-teal mb-4 animate-fade-up">About Jagdale Air</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6 animate-fade-up delay-100" style={{ fontFamily: 'Inter' }} data-testid="about-title">
            Air is Changing.
          </h1>
          <p className="text-base lg:text-lg leading-relaxed max-w-2xl mx-auto animate-fade-up delay-200" style={{ color: 'var(--text-secondary)' }}>
            We started with a simple observation: the air we breathe indoors is often worse than the air outside.
            Jagdale Air was founded to solve this fundamental problem — not with filters alone, but with intelligence.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding px-6 lg:px-12" data-testid="about-story">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Inter' }}>
                From engineering<br />to revolution.
              </h2>
              <div className="space-y-4 text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                <p>Jagdale Air began in Mumbai, where opening a window meant inviting the city's pollution into your home. We asked: <em>What if a window could think?</em></p>
                <p>Our team of engineers, data scientists, and environmental researchers spent two years developing Vayu — a complete air quality ecosystem that doesn't just filter air, but understands it.</p>
                <p>Today, Vayu represents the convergence of hardware innovation and intelligent software — designed to make clean air accessible, automatic, and affordable.</p>
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1737280956555-ad3430729812?w=600&q=75" alt="Modern architecture" className="rounded-3xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding px-6 lg:px-12 bg-teal-gradient relative overflow-hidden" data-testid="about-values">
        <AirParticles count={12} color="rgba(63,167,214,0.08)" />
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Inter' }}>What drives us.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Target, title: 'Mission', desc: 'Make clean indoor air a basic right, not a luxury. Affordable, intelligent, and accessible to every building.', color: 'var(--aqua)' },
              { icon: Eye, title: 'Vision', desc: 'A world where every building breathes intelligently. Where the air inside is always better than the air outside.', color: '#10B981' },
              { icon: Users, title: 'Impact', desc: 'Reducing indoor pollution on average through smart filtration. Building the infrastructure for cleaner cities across India.', color: '#F59E0B' },
            ].map((v) => (
              <div key={v.title} className="text-center p-8 rounded-2xl flex flex-col items-center justify-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', minHeight: 220 }} data-testid={`value-${v.title}`}>
                <v.icon className="w-8 h-8 mb-4" style={{ color: v.color }} strokeWidth={1.5} />
                <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Inter' }}>{v.title}</h3>
                <p className="text-sm leading-relaxed text-white/50">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
